import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatchApp } from "../../../../lib/redux";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  startGetAdsCampaignTree,
  startGetStatisticsAdsCampaign,
  startUpdateCampaignEntityStatus,
} from "../../../../actions/adsCampaigns";
import { LaunchError } from "../../../../utils/errors";
import {
  ADS_CAMPAIGN_ENTITIES,
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_STATUS_DEACTIVATED,
} from "../../../../utils/constants";
import { buildOptimisticSwitchCopy } from "../../../../optimisticSwitch/copy";
import { OPTIMISTIC_SWITCH_KIND } from "../../../../optimisticSwitch/kinds";
import {
  runOptimisticSwitch,
  toOptimisticSwitchPayload,
} from "../../../../optimisticSwitch/runOptimisticSwitch";
import {
  CAMPAIGN_TABLE_ENTITY_KIND,
  CAMPAIGN_TABLE_LOAD_PHASE,
  getCampaignTreeStatisticsCacheKey,
  isCampaignTableLoadSettled,
  withCampaignTreeEntityStatus,
} from "./campaignTableTree";

const beginsGeneration = (generations, cacheKey) => {
  const generation = (generations[cacheKey] || 0) + 1;
  generations[cacheKey] = generation;
  return generation;
};

const isCurrentGeneration = (generations, cacheKey, generation) =>
  generations[cacheKey] === generation;

const OPTIMISTIC_KIND_BY_ENTITY = {
  [CAMPAIGN_TABLE_ENTITY_KIND.AD_GROUP]: OPTIMISTIC_SWITCH_KIND.AD_GROUP,
  [CAMPAIGN_TABLE_ENTITY_KIND.ASSETS_GROUP]:
    OPTIMISTIC_SWITCH_KIND.ASSETS_GROUP,
  [CAMPAIGN_TABLE_ENTITY_KIND.AD]: OPTIMISTIC_SWITCH_KIND.AD,
};

const API_ENTITY_BY_KIND = {
  [CAMPAIGN_TABLE_ENTITY_KIND.AD_GROUP]: ADS_CAMPAIGN_ENTITIES.AD_GROUP,
  [CAMPAIGN_TABLE_ENTITY_KIND.ASSETS_GROUP]: ADS_CAMPAIGN_ENTITIES.ASSETS_GROUP,
  [CAMPAIGN_TABLE_ENTITY_KIND.AD]: ADS_CAMPAIGN_ENTITIES.AD,
};

const writeEntries = ({ entriesRef, setEntries, updater }) => {
  const next = updater(entriesRef.current);
  entriesRef.current = next;
  setEntries(next);
};

// Entries are keyed by campaign (not by date range): the entity tree does not
// depend on the range, so changing the filter must never lose it. Only the
// statistics are range-scoped, and they are tracked separately inside the
// entry through `statisticsKey` / `statsPhase`.
const useCampaignTableTree = ({ dateRange = null } = {}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [expanded, setExpanded] = useState({});
  const [entries, setEntries] = useState({});
  const expandedRef = useRef({});
  const entriesRef = useRef({});
  const treeGenerationsRef = useRef({});
  const statisticsGenerationsRef = useRef({});
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  const patchEntry = useCallback(({ campaignID, updater }) => {
    writeEntries({
      entriesRef,
      setEntries,
      updater: (current) => ({
        ...current,
        [campaignID]: updater(current[campaignID] || {}),
      }),
    });
  }, []);

  const loadTree = useCallback(
    async ({ campaignID, force = false }) => {
      const currentPhase = entriesRef.current[campaignID]?.treePhase;
      if (!force && isCampaignTableLoadSettled(currentPhase)) return;

      const generation = beginsGeneration(
        treeGenerationsRef.current,
        campaignID
      );

      patchEntry({
        campaignID,
        updater: (existing) => ({
          ...existing,
          treePhase: CAMPAIGN_TABLE_LOAD_PHASE.LOADING,
        }),
      });

      try {
        const treeResult = await dispatch(
          startGetAdsCampaignTree({ campaignID })
        );

        if (
          !isCurrentGeneration(
            treeGenerationsRef.current,
            campaignID,
            generation
          )
        ) {
          return;
        }

        patchEntry({
          campaignID,
          updater: (existing) => ({
            ...existing,
            treePhase: treeResult?.ok
              ? CAMPAIGN_TABLE_LOAD_PHASE.READY
              : CAMPAIGN_TABLE_LOAD_PHASE.ERROR,
            campaign: treeResult?.ok
              ? treeResult.data
              : (existing.campaign ?? null),
          }),
        });
      } catch (error) {
        if (
          !isCurrentGeneration(
            treeGenerationsRef.current,
            campaignID,
            generation
          )
        ) {
          return;
        }

        dispatch(
          LaunchError(error, false, [], {
            functionName: "useCampaignTableTree",
            context: { campaignID },
            tags: { action: "getAdsCampaignTree" },
            level: "error",
          })
        );

        patchEntry({
          campaignID,
          updater: (existing) => ({
            ...existing,
            treePhase: CAMPAIGN_TABLE_LOAD_PHASE.ERROR,
          }),
        });
      }
    },
    [dispatch, patchEntry]
  );

  const loadStatistics = useCallback(
    async ({ campaignID, range, force = false }) => {
      const statisticsKey = getCampaignTreeStatisticsCacheKey({
        campaignID,
        dateRange: range,
      });
      const entry = entriesRef.current[campaignID];
      const alreadyLoaded =
        entry?.statisticsKey === statisticsKey &&
        isCampaignTableLoadSettled(entry?.statsPhase);
      if (!force && alreadyLoaded) return;

      const generation = beginsGeneration(
        statisticsGenerationsRef.current,
        statisticsKey
      );

      patchEntry({
        campaignID,
        updater: (existing) => ({
          ...existing,
          statisticsKey,
          statsPhase: CAMPAIGN_TABLE_LOAD_PHASE.LOADING,
          statistics: null,
        }),
      });

      try {
        const statsResult = await dispatch(
          startGetStatisticsAdsCampaign({
            campaignID,
            startDateTime: range?.startDateTime,
            endDateTime: range?.endDateTime,
          })
        );

        if (
          !isCurrentGeneration(
            statisticsGenerationsRef.current,
            statisticsKey,
            generation
          )
        ) {
          return;
        }

        patchEntry({
          campaignID,
          updater: (existing) => ({
            ...existing,
            statisticsKey,
            statsPhase: statsResult?.ok
              ? CAMPAIGN_TABLE_LOAD_PHASE.READY
              : CAMPAIGN_TABLE_LOAD_PHASE.ERROR,
            statistics: statsResult?.ok ? statsResult.data?.total || {} : null,
          }),
        });
      } catch (error) {
        if (
          !isCurrentGeneration(
            statisticsGenerationsRef.current,
            statisticsKey,
            generation
          )
        ) {
          return;
        }

        dispatch(
          LaunchError(error, false, [], {
            functionName: "useCampaignTableTree",
            context: { campaignID },
            tags: { action: "getStatisticsAdsCampaign" },
            level: "error",
          })
        );

        patchEntry({
          campaignID,
          updater: (existing) => ({
            ...existing,
            statisticsKey,
            statsPhase: CAMPAIGN_TABLE_LOAD_PHASE.ERROR,
            statistics: null,
          }),
        });
      }
    },
    [dispatch, patchEntry]
  );

  const loadEntry = useCallback(
    ({ campaignID, range, force = false }) => {
      // Fire-and-forget on purpose: the entity tree settles first and the
      // metric cells stay in skeleton until the statistics arrive.
      loadTree({ campaignID, force });
      loadStatistics({ campaignID, range, force });
    },
    [loadTree, loadStatistics]
  );

  const rangeStart = dateRange?.startDateTime;
  const rangeEnd = dateRange?.endDateTime;
  const rangeStartToken =
    rangeStart instanceof Date ? rangeStart.getTime() : (rangeStart ?? null);
  const rangeEndToken =
    rangeEnd instanceof Date ? rangeEnd.getTime() : (rangeEnd ?? null);

  useEffect(() => {
    const openCampaignIDs = Object.keys(expandedRef.current).filter(
      (campaignID) => expandedRef.current[campaignID]
    );
    // A range change only invalidates the metrics: the entities are already
    // rendered and must stay on screen while the new numbers load. If the tree
    // never loaded it is retried here as well, since this is the only refresh
    // point for an already expanded campaign.
    openCampaignIDs.forEach((campaignID) => {
      const entry = entriesRef.current[campaignID];
      const treeMissing =
        entry?.treePhase !== CAMPAIGN_TABLE_LOAD_PHASE.READY &&
        entry?.treePhase !== CAMPAIGN_TABLE_LOAD_PHASE.LOADING;
      if (treeMissing) {
        loadTree({ campaignID });
      }
      loadStatistics({
        campaignID,
        range: dateRangeRef.current,
        force: true,
      });
    });
  }, [rangeStartToken, rangeEndToken, loadStatistics, loadTree]);

  const onToggleCampaignTree = ({ campaignID }) => {
    const nextOpen = !expandedRef.current[campaignID];
    expandedRef.current = {
      ...expandedRef.current,
      [campaignID]: nextOpen,
    };
    setExpanded(expandedRef.current);
    if (nextOpen) {
      loadEntry({ campaignID, range: dateRangeRef.current });
    }
  };

  const onRetryCampaignTree = ({ campaignID, scope = "all" }) => {
    if (scope === "statistics") {
      loadStatistics({
        campaignID,
        range: dateRangeRef.current,
        force: true,
      });
      return;
    }

    loadEntry({
      campaignID,
      range: dateRangeRef.current,
      force: true,
    });
  };

  const applyEntityStatus = ({
    campaignID,
    kind,
    entityID,
    adGroupID,
    status,
  }) => {
    writeEntries({
      entriesRef,
      setEntries,
      updater: (current) => {
        const entry = current[campaignID];
        if (!entry?.campaign) return current;
        return {
          ...current,
          [campaignID]: {
            ...entry,
            campaign: withCampaignTreeEntityStatus({
              campaign: entry.campaign,
              kind,
              entityID,
              adGroupID,
              status,
            }),
          },
        };
      },
    });
  };

  const onChangeEntityStatus = async ({
    campaignID,
    platform,
    kind,
    entityID,
    adGroupID,
    name,
    status,
  }) => {
    if (
      status !== ADS_CAMPAIGN_STATUS_ACTIVE &&
      status !== ADS_CAMPAIGN_STATUS_DEACTIVATED
    ) {
      return false;
    }

    const nextStatus =
      status === ADS_CAMPAIGN_STATUS_ACTIVE
        ? ADS_CAMPAIGN_STATUS_DEACTIVATED
        : ADS_CAMPAIGN_STATUS_ACTIVE;
    const optimisticKind = OPTIMISTIC_KIND_BY_ENTITY[kind];
    const entity = API_ENTITY_BY_KIND[kind];
    if (!optimisticKind || !entity) return false;

    const copy = buildOptimisticSwitchCopy({
      t,
      kind: optimisticKind,
      name,
      activate: nextStatus === ADS_CAMPAIGN_STATUS_ACTIVE,
    });

    const response = await runOptimisticSwitch({
      copy,
      applyOptimistic: () => {
        applyEntityStatus({
          campaignID,
          kind,
          entityID,
          adGroupID,
          status: nextStatus,
        });
      },
      revertOptimistic: () => {
        applyEntityStatus({
          campaignID,
          kind,
          entityID,
          adGroupID,
          status,
        });
      },
      action: ({ operationId }) =>
        dispatch(
          startUpdateCampaignEntityStatus({
            campaignID,
            platform,
            entity,
            entityID,
            status: nextStatus,
            optimisticSwitch: toOptimisticSwitchPayload({ operationId, copy }),
          })
        ),
    });

    return response.ok;
  };

  return {
    expanded,
    entries,
    onToggleCampaignTree,
    onRetryCampaignTree,
    onChangeEntityStatus,
  };
};

export default useCampaignTableTree;
